import { useState, useEffect, useCallback } from 'react';
import { PropertyPhoto, ListingsByLanguage, PropertyFormData, UserProfile, DescriptionLanguage } from '@/types/property';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Check, Download, RefreshCw, Facebook, Share2, ArrowLeft, Sparkles, Instagram, ImageIcon, Save, Globe, SlidersHorizontal, Edit3, MessageCircle, Phone, Send, QrCode, ExternalLink, ChevronLeft, ChevronRight, RotateCcw, User, Video, Play, Eye, Crown, FileText, Languages, Lock, Calendar, Mail, MessageSquare, Loader2, BarChart3 } from 'lucide-react';
import { generatePremiumDescription } from '@/lib/generatePremiumDescription';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { PhotoComparisonSlider } from '@/components/PhotoComparisonSlider';
import { saleFeatureCategories, rentFeatureCategories } from '@/lib/featureCategories';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { useBilling } from '@/hooks/useBilling';
import { hasPreviewMicrositeAccess, hasPremiumDescriptionAccess, TIER_CONFIGS, formatPrice } from '@/types/billing';
import { useGeoCurrency } from '@/hooks/useGeoCurrency';
import { supabase } from '@/integrations/supabase/client';
import { getDataFastAttribution } from '@/lib/datafast';
import { generateListingFlyer, generateFlyerPreview, downloadFlyer } from '@/lib/generateListingFlyer';
import { WatermarkOverlay } from '@/components/WatermarkOverlay';
import { TrialProtectedContent } from '@/components/TrialProtectedContent';
import { isTrialUser } from '@/lib/trialUtils';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import JSZip from 'jszip';
import { getMarketConfig } from '@/lib/markets';
import type { CinematicClip } from '@/lib/generateCinematicVideo';

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', AED: 'AED ', AUD: 'A$', CAD: 'C$',
};

interface ListingResultsProps {
  formData: PropertyFormData;
  photos: PropertyPhoto[];
  listingsByLanguage: ListingsByLanguage[];
  profile?: UserProfile | null;
  listingId?: string;
  profilePictureUrl?: string | null;
  videoUrl?: string | null;
  createdAt?: string | null; // Listing creation timestamp
  isTrial?: boolean; // Whether this is a trial listing
  descriptionImageUrl?: string | null; // Watermarked description image shown for trials (no copyable text in DOM)
  onRegenerate: () => void;
  onStartOver: () => void;
  onSave?: (editedListings: ListingsByLanguage[]) => void;
  onReEnhancePhoto?: (photoId: string) => Promise<void>;
  onRevertPhoto?: (photoId: string, target: 'reenhanced' | 'enhanced' | 'original') => Promise<void>;
  onRegenerateVideo?: () => Promise<void>;
  isRegenerating: boolean;
  isSaving?: boolean;
  reEnhancingPhotoId?: string;
  reEnhancedPhotoIds?: Set<string>;
  photosReEnhanced?: boolean;
  isRegeneratingVideo?: boolean;
  // Cinematic AI Video add-on
  cinematicAddon?: {
    purchased: boolean;
    used: number;
    paid: number;
    isTrial: boolean;
    resolution?: '720p' | '1080p';
    clipRegensUsed?: number;
    clipRegensAllowance?: number;
  };
  cinematicProgress?: { done: number; total: number; stage: 'idle' | 'clips' | 'stitching' };
  isGeneratingCinematic?: boolean;
  onGenerateCinematic?: (resolution?: '720p' | '1080p') => Promise<void> | void;
  cinematicStyle?: 'walkthrough' | 'drone';
  onCinematicStyleChange?: (style: 'walkthrough' | 'drone') => void;
  cinematicClips?: CinematicClip[];
  regeneratingClipIndex?: number | null;
  onRegenerateClip?: (clipIndex: number) => Promise<void> | void;
  onUpgradeCinematicResolution?: () => Promise<void> | void;
}

const languageLabels: Record<string, { label: string; flag: string }> = {
  en: { label: 'English', flag: '🇬🇧' },
  el: { label: 'Greek', flag: '🇬🇷' },
  ru: { label: 'Russian', flag: '🇷🇺' },
  he: { label: 'Hebrew', flag: '🇮🇱' },
  de: { label: 'German', flag: '🇩🇪' },
  es: { label: 'Spanish', flag: '🇪🇸' },
  fr: { label: 'French', flag: '🇫🇷' },
  it: { label: 'Italian', flag: '🇮🇹' },
  pt: { label: 'Portuguese', flag: '🇵🇹' },
  nl: { label: 'Dutch', flag: '🇳🇱' },
  pl: { label: 'Polish', flag: '🇵🇱' },
  ar: { label: 'Arabic', flag: '🇸🇦' },
  tr: { label: 'Turkish', flag: '🇹🇷' },
  zh: { label: 'Chinese', flag: '🇨🇳' },
  ja: { label: 'Japanese', flag: '🇯🇵' },
  ko: { label: 'Korean', flag: '🇰🇷' },
  uk: { label: 'Ukrainian', flag: '🇺🇦' },
  hi: { label: 'Hindi', flag: '🇮🇳' },
  th: { label: 'Thai', flag: '🇹🇭' },
  vi: { label: 'Vietnamese', flag: '🇻🇳' },
  sv: { label: 'Swedish', flag: '🇸🇪' },
  no: { label: 'Norwegian', flag: '🇳🇴' },
  da: { label: 'Danish', flag: '🇩🇰' },
  fi: { label: 'Finnish', flag: '🇫🇮' },
  cs: { label: 'Czech', flag: '🇨🇿' },
  ro: { label: 'Romanian', flag: '🇷🇴' },
  hu: { label: 'Hungarian', flag: '🇭🇺' },
  bg: { label: 'Bulgarian', flag: '🇧🇬' },
};

// Photo Dialog with navigation
function PhotoDialogContent({ 
  photos, 
  currentIndex, 
  onDownload,
  isTrial = false
}: { 
  photos: PropertyPhoto[]; 
  currentIndex: number; 
  onDownload: (photo: PropertyPhoto, index: number) => void;
  isTrial?: boolean;
}) {
  const [viewIndex, setViewIndex] = useState(currentIndex);
  const photo = photos[viewIndex];
  // Show comparison slider only if we have both original and enhanced, and they're different
  // Also don't show slider if original is missing (we only have enhanced photo)
  const hasEnhancement = photo?.enhanced && photo.enhanced !== photo.preview && !photo.originalMissing;
  
  // Reset to currentIndex when dialog opens with new photo
  useEffect(() => {
    setViewIndex(currentIndex);
  }, [currentIndex]);
  
  const goToPrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setViewIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1));
  }, [photos.length]);
  
  const goToNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setViewIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0));
  }, [photos.length]);
  
  // Swipe navigation for touch devices
  const { onTouchStart: swipeTouchStart, onTouchEnd: swipeTouchEnd } = useSwipeNavigation({
    onSwipeLeft: goToNext,
    onSwipeRight: goToPrev,
    minDistance: 60,
  });
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [photos.length]);

  if (!photo) return null;

  return (
    <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden">
      <DialogHeader className="p-3 sm:p-4 pb-0">
        <DialogTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
            <span className="text-sm sm:text-base">Photo {viewIndex + 1} of {photos.length}</span>
            {hasEnhancement && (
              <span className="text-xs font-normal text-muted-foreground hidden sm:inline">
                — Drag slider to compare
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => goToPrev(e)}
              className="h-10 w-10 min-h-[44px] min-w-[44px]"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => goToNext(e)}
              className="h-10 w-10 min-h-[44px] min-w-[44px]"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </DialogTitle>
        <DialogDescription className="sr-only">
          View and compare original and enhanced property photos
        </DialogDescription>
      </DialogHeader>
      
      <div className="relative p-3 sm:p-4 pt-2" onTouchStart={swipeTouchStart} onTouchEnd={swipeTouchEnd}>
        {isTrial ? (
          <WatermarkOverlay className="w-full rounded-xl">
            {hasEnhancement ? (
              <PhotoComparisonSlider
                key={`slider-${viewIndex}-${photo.id}`}
                originalSrc={photo.preview}
                enhancedSrc={photo.enhanced!}
              />
            ) : (
              <div className="w-full rounded-xl overflow-hidden bg-muted">
                <img 
                  src={photo.preview} 
                  alt={`Property photo ${viewIndex + 1}`}
                  className="w-full h-auto"
                />
              </div>
            )}
          </WatermarkOverlay>
        ) : hasEnhancement ? (
          <PhotoComparisonSlider
            key={`slider-${viewIndex}-${photo.id}`}
            originalSrc={photo.preview}
            enhancedSrc={photo.enhanced!}
          />
        ) : (
          <div className="w-full rounded-xl overflow-hidden bg-muted">
            <img 
              src={photo.preview} 
              alt={`Property photo ${viewIndex + 1}`}
              className="w-full h-auto"
            />
            <p className="text-center text-muted-foreground mt-2 text-sm">
              No enhancement available for this photo
            </p>
          </div>
        )}
        
        {/* Navigation overlays - larger on mobile */}
        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => goToPrev(e)}
              className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-10 sm:h-10 min-h-[44px] min-w-[44px] bg-background/80 hover:bg-background active:bg-background rounded-full flex items-center justify-center shadow-lg transition-colors z-[70]"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => goToNext(e)}
              className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-10 sm:h-10 min-h-[44px] min-w-[44px] bg-background/80 hover:bg-background active:bg-background rounded-full flex items-center justify-center shadow-lg transition-colors z-[70]"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
      
      {/* Thumbnail strip - larger on mobile */}
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-3 sm:px-4 pb-3 sm:pb-4 scrollbar-none">
          {photos.map((p, idx) => (
            <button
              key={p.id}
              onClick={() => setViewIndex(idx)}
              className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 min-w-[48px] min-h-[48px] rounded-lg overflow-hidden transition-all ${
                idx === viewIndex 
                  ? 'ring-2 ring-primary ring-offset-2' 
                  : 'opacity-60 hover:opacity-100 active:opacity-100'
              }`}
            >
              <img 
                src={p.enhanced || p.preview} 
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
      
      <div className="flex justify-end gap-2 p-3 sm:p-4 pt-0">
        {hasEnhancement && (
          <p className="text-xs text-muted-foreground self-center mr-auto sm:hidden">
            Drag to compare
          </p>
        )}
        <Button variant="outline" className="min-h-[44px]" onClick={() => onDownload(photo, viewIndex)}>
          <Download className="w-4 h-4 mr-2" />
          Download
        </Button>
      </div>
    </DialogContent>
  );
}
export function ListingResults({ 
  formData, 
  photos, 
  listingsByLanguage: initialListingsByLanguage, 
  profile,
  listingId,
  profilePictureUrl,
  videoUrl,
  createdAt,
  isTrial: isTrialProp,
  descriptionImageUrl,
  onRegenerate,
  onStartOver,
  onSave,
  onReEnhancePhoto,
  onRevertPhoto,
  onRegenerateVideo,
  isRegenerating,
  isSaving,
  reEnhancingPhotoId,
  reEnhancedPhotoIds,
  photosReEnhanced,
  isRegeneratingVideo,
  cinematicAddon,
  cinematicProgress,
  isGeneratingCinematic,
  onGenerateCinematic,
  cinematicStyle = 'walkthrough',
  onCinematicStyleChange,
  cinematicClips = [],
  regeneratingClipIndex = null,
  onRegenerateClip,
  onUpgradeCinematicResolution,
}: ListingResultsProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(initialListingsByLanguage[0]?.language || 'en');
  const [listingsByLanguage, setListingsByLanguage] = useState<ListingsByLanguage[]>(initialListingsByLanguage);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [isDownloadingPhotos, setIsDownloadingPhotos] = useState(false);
  const [flyerLanguage, setFlyerLanguage] = useState<DescriptionLanguage>(initialListingsByLanguage[0]?.language || 'en');
  const [isGeneratingFlyer, setIsGeneratingFlyer] = useState(false);
  const [flyerPreviewUrl, setFlyerPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingPremium, setIsGeneratingPremium] = useState(false);
  const [cinematicResolution, setCinematicResolution] = useState<'720p' | '1080p'>('720p');

  // Get billing state to check microsite access and trial status
  const { billing, isLoading: isBillingLoading } = useBilling();
  const hasMicrositeAccess = billing ? hasPreviewMicrositeAccess(billing) : false;
  const hasPremiumAccess = billing ? hasPremiumDescriptionAccess(billing) : false;
  // Determine if this is a trial listing - ALWAYS use the database flag (isTrialProp) if provided
  // The is_trial column is set at listing creation and is the source of truth
  // Only fall back to billing check if prop is undefined AND billing is fully loaded with active subscription
  const isTrial = (() => {
    // If we have the database flag, trust it completely
    if (isTrialProp !== undefined) return isTrialProp;
    
    // Still loading billing - don't show trial message yet
    if (isBillingLoading || !billing) return false;
    
    // User has active subscription - never show trial
    if (billing.subscriptionStatus === 'active') return false;
    
    // Fallback to trialUtils check for edge cases
    return isTrialUser(billing);
  })();

  // One-click unlock: send trial users straight to the Single Property Kit
  // checkout instead of detouring through the pricing page. Completing the
  // purchase unlocks every trial listing server-side (unlockTrialListings).
  const [isUnlockingKit, setIsUnlockingKit] = useState(false);
  const { currency: checkoutCurrency } = useGeoCurrency();
  const kitPrice = formatPrice(TIER_CONFIGS.one_off_kit.priceCents);

  const handleUnlockKitCheckout = async () => {
    if (isUnlockingKit) return;
    setIsUnlockingKit(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
      const locale = navigator.languages?.[0] || navigator.language || undefined;
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          planType: 'try_it_kit',
          preferredCurrency: checkoutCurrency,
          timezone,
          locale,
          ...getDataFastAttribution(),
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Kit checkout error:', err);
      toast({
        title: 'Checkout failed',
        description: 'Could not start checkout. Please try again or visit the pricing page.',
        variant: 'destructive',
      });
    } finally {
      setIsUnlockingKit(false);
    }
  };

  // --- Trial gate -------------------------------------------------------------
  // A trial listing is a watermarked *preview*: the user can SEE full quality but
  // must pay (subscribe or buy the $15 kit) before any clean, usable deliverable
  // (photos, description text, flyer, video) can be downloaded, copied, or shared.
  // Every handler that emits a clean asset routes through this guard.
  const blockIfTrial = (): boolean => {
    if (isTrial === true) {
      toast({
        title: "Locked while on trial",
        description: "Subscribe or unlock this listing to download, copy, and share full-quality assets without watermarks.",
        variant: "destructive",
      });
      return true;
    }
    return false;
  };

  // Sync when initialListingsByLanguage changes (e.g., after regenerate)
  useEffect(() => {
    setListingsByLanguage(initialListingsByLanguage);
  }, [initialListingsByLanguage]);

  // Generate flyer preview on mount and when language changes
  // Only generate if profilePictureUrl is provided (meaning user selected PDF flyer option)
  useEffect(() => {
    const generatePreview = async () => {
      // Skip if user didn't select the PDF flyer option during creation
      if (!profilePictureUrl) {
        setFlyerPreviewUrl(null);
        return;
      }
      
      if (photos.length === 0) return;
      
      setIsLoadingPreview(true);
      try {
        // Get description in first available language for preview
        const previewLanguage = initialListingsByLanguage[0]?.language || 'en';
        const langContent = initialListingsByLanguage.find(l => l.language === previewLanguage);
        const generalListing = langContent?.listings?.find(l => l.platform === 'general');
        const description = generalListing?.description || '';

        const enhancedUrls = photos
          .map(p => p.enhanced || p.preview)
          .filter(Boolean);

        const result = await generateFlyerPreview({
          photos: enhancedUrls,
          propertyType: formData.type,
          city: formData.city,
          address: formData.address,
          price: formData.price,
          bedrooms: formData.bedrooms,
          bathrooms: formData.bathrooms,
          squareMeters: formData.squareMeters,
          listingType: formData.listingType,
          features: formData.features,
          description: description,
          agentName: profile?.agency_name,
          agentPhone: profile?.phone,
          agencyName: profile?.agency_name,
          language: previewLanguage,
          currency: getMarketConfig(formData.market).currency,
          areaUnit: getMarketConfig(formData.market).areaUnit,
        });

        if (result?.url) {
          setFlyerPreviewUrl(result.url);
        }
      } catch (err) {
        console.warn('Failed to generate flyer preview:', err);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    generatePreview();
  }, [photos, formData, profile, initialListingsByLanguage, profilePictureUrl]);

  // Generate and download flyer in selected language
  const generateAndDownloadFlyer = async (language: DescriptionLanguage) => {
    if (blockIfTrial()) return;
    setIsGeneratingFlyer(true);
    try {
      // Get description in selected language
      const langContent = listingsByLanguage.find(l => l.language === language);
      const generalListing = langContent?.listings?.find(l => l.platform === 'general');
      const description = generalListing?.description || '';

      const enhancedUrls = photos
        .map(p => p.enhanced || p.preview)
        .filter(Boolean);

      const result = await generateListingFlyer({
        photos: enhancedUrls,
        propertyType: formData.type,
        city: formData.city,
        address: formData.address,
        price: formData.price,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        squareMeters: formData.squareMeters,
        listingType: formData.listingType,
        features: formData.features,
        description: description,
        agentName: profile?.agency_name,
        agentPhone: profile?.phone,
        agentEmail: profile?.email,
        language: language,
        currency: getMarketConfig(formData.market).currency,
        areaUnit: getMarketConfig(formData.market).areaUnit,
      });

      if (result.success && result.blob) {
        downloadFlyer(result.blob, `property-flyer-${language}-${listingId || 'listing'}.pdf`);
        toast({
          title: "Flyer Downloaded!",
          description: `PDF flyer generated in ${languageLabels[language]?.label || language}`,
        });
      } else {
        throw new Error(result.error || 'Failed to generate flyer');
      }
    } catch (err) {
      console.error('Flyer generation failed:', err);
      toast({
        title: "Error",
        description: "Failed to generate flyer",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingFlyer(false);
    }
  };

  const getContactInfo = () => {
    if (!profile) return '';
    const parts: string[] = [];
    if (profile.agency_name) parts.push(`🏢 ${profile.agency_name}`);
    if (profile.license_number) parts.push(`📋 License: ${profile.license_number}`);
    if (profile.phone) parts.push(`📞 ${profile.phone}`);
    if (profile.email) parts.push(`✉️ ${profile.email}`);
    return parts.length > 0 ? `\n\n---\n${parts.join('\n')}` : '';
  };

  const copyToClipboard = async (text: string, id: string) => {
    if (blockIfTrial()) return;

    const fullText = text + getContactInfo();
    await navigator.clipboard.writeText(fullText);
    setCopiedId(id);
    toast({
      title: "Copied!",
      description: "Listing copied to clipboard with contact info",
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Share functions for different platforms
  const shareToWhatsApp = (text: string) => {
    if (blockIfTrial()) return;
    const fullText = text + getContactInfo();
    const encodedText = encodeURIComponent(fullText);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
    toast({ title: "Opening WhatsApp", description: "Share your listing via WhatsApp" });
  };

  const shareToViber = (text: string) => {
    if (blockIfTrial()) return;
    const fullText = text + getContactInfo();
    const encodedText = encodeURIComponent(fullText);
    window.open(`viber://forward?text=${encodedText}`, '_blank');
    toast({ title: "Opening Viber", description: "Share your listing via Viber" });
  };

  const shareToFacebookPost = async (text: string) => {
    if (blockIfTrial()) return;
    const fullText = text + getContactInfo();
    // First copy to clipboard since Facebook doesn't support pre-filled text
    await navigator.clipboard.writeText(fullText);
    window.open('https://www.facebook.com/', '_blank');
    toast({ 
      title: "Text copied!", 
      description: "Paste your listing in Facebook. Text has been copied to clipboard." 
    });
  };

  const shareToInstagram = async (text: string) => {
    if (blockIfTrial()) return;
    const fullText = text + getContactInfo();
    // Instagram doesn't have a web share API, so we copy and open Instagram
    await navigator.clipboard.writeText(fullText);
    window.open('https://www.instagram.com/', '_blank');
    toast({ 
      title: "Text copied!", 
      description: "Paste your caption in Instagram. Text has been copied to clipboard." 
    });
  };

  const shareToInstagramStory = async () => {
    if (blockIfTrial()) return;
    // For stories, user needs to download photo and manually upload
    if (photos.length > 0) {
      const photo = photos[0];
      downloadPhoto(photo, 0);
      const storyText = instagramStory?.description || '';
      await navigator.clipboard.writeText(storyText + getContactInfo());
      toast({ 
        title: "Photo downloaded & text copied!", 
        description: "Upload the photo to your Instagram Story and paste the text." 
      });
    }
  };

  const shareViaWebShare = async (title: string, text: string) => {
    if (blockIfTrial()) return;
    const fullText = text + getContactInfo();
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: fullText,
        });
        toast({ title: "Shared!", description: "Listing shared successfully" });
      } catch (err) {
        // User cancelled or error
        if ((err as Error).name !== 'AbortError') {
          await copyToClipboard(text, 'web-share-fallback');
        }
      }
    } else {
      await copyToClipboard(text, 'web-share-fallback');
    }
  };

  // Share with photos function
  const shareWithPhotos = async (platform: 'whatsapp' | 'viber' | 'facebook' | 'instagram' | 'native') => {
    if (blockIfTrial()) return;
    const currentListing = facebookListing || generalListing;
    if (!currentListing) return;

    const fullText = `${currentListing.title}\n\n${currentListing.description}${getContactInfo()}`;
    
    // First download all enhanced photos
    setIsDownloadingPhotos(true);
    const enhancedPhotos = photos.filter(p => p.enhanced && p.enhanced !== p.preview);
    
    if (enhancedPhotos.length > 0) {
      enhancedPhotos.forEach((photo, index) => {
        setTimeout(() => downloadPhoto(photo, index), index * 200);
      });
      
      // Wait for downloads to initiate
      await new Promise(resolve => setTimeout(resolve, enhancedPhotos.length * 200 + 500));
    }
    setIsDownloadingPhotos(false);

    // Then share text based on platform
    switch (platform) {
      case 'whatsapp':
        shareToWhatsApp(fullText);
        break;
      case 'viber':
        shareToViber(fullText);
        break;
      case 'facebook':
        await navigator.clipboard.writeText(fullText);
        window.open('https://www.facebook.com/', '_blank');
        toast({ 
          title: "Photos downloaded & text copied!", 
          description: "Upload your enhanced photos and paste the listing text in Facebook." 
        });
        break;
      case 'instagram':
        await navigator.clipboard.writeText(fullText);
        window.open('https://www.instagram.com/', '_blank');
        toast({ 
          title: "Photos downloaded & text copied!", 
          description: "Upload your enhanced photos and paste the caption in Instagram." 
        });
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({ title: currentListing.title, text: fullText });
            toast({ title: "Shared!", description: "Listing shared successfully" });
          } catch (err) {
            if ((err as Error).name !== 'AbortError') {
              await navigator.clipboard.writeText(fullText);
              toast({ title: "Copied!", description: "Text copied to clipboard" });
            }
          }
        } else {
          await navigator.clipboard.writeText(fullText);
          toast({ title: "Copied!", description: "Text copied to clipboard" });
        }
        break;
    }
  };

  // QR Code functions
  const getPreviewUrl = () => {
    if (!listingId) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/preview/${listingId}`;
  };

  // Trackable target encoded into the QR code. Routes scans through /qr/:id, which
  // records a scan event and then redirects to the microsite, so every scan is
  // measurable in the QR Analytics page. The ?src=qr label marks the medium.
  const getQrTargetUrl = () => {
    if (!listingId) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/qr/${listingId}?src=qr`;
  };

  const getQrCodeUrl = (size: number = 300) => {
    const targetUrl = getQrTargetUrl();
    if (!targetUrl) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(targetUrl)}&format=png&margin=10`;
  };

  const downloadQrCode = async () => {
    if (blockIfTrial()) return;
    const qrUrl = getQrCodeUrl(600); // Higher resolution for download
    if (!qrUrl) return;
    
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `property-qr-${listingId}.png`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast({ title: "Downloaded!", description: "QR code saved to your device" });
    } catch (error) {
      toast({ title: "Error", description: "Could not download QR code", variant: "destructive" });
    }
  };

  const copyPreviewLink = async () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) return;
    await navigator.clipboard.writeText(previewUrl);
    toast({ title: "Copied!", description: "Preview link copied to clipboard" });
  };

  const openPreviewPage = () => {
    const previewUrl = getPreviewUrl();
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  // Share preview link to different platforms
  const sharePreviewToWhatsApp = () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    const generalListing = listingsByLanguage[0]?.listings?.find(l => l.platform === 'general');
    const title = generalListing?.title || 'Check out this property!';
    const message = `${title}\n\n${previewUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    toast({ title: "Opening WhatsApp", description: "Share your listing via WhatsApp" });
  };

  const sharePreviewToViber = () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    const generalListing = listingsByLanguage[0]?.listings?.find(l => l.platform === 'general');
    const title = generalListing?.title || 'Check out this property!';
    const message = `${title}\n\n${previewUrl}`;
    window.open(`viber://forward?text=${encodeURIComponent(message)}`, '_blank');
    toast({ title: "Opening Viber", description: "Share your listing via Viber" });
  };

  const sharePreviewToTelegram = () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    const generalListing = listingsByLanguage[0]?.listings?.find(l => l.platform === 'general');
    const title = generalListing?.title || 'Check out this property!';
    window.open(`https://t.me/share/url?url=${encodeURIComponent(previewUrl)}&text=${encodeURIComponent(title)}`, '_blank');
    toast({ title: "Opening Telegram", description: "Share your listing via Telegram" });
  };

  const sharePreviewToFacebook = () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    // Facebook Sharer URL - opens share dialog with the preview URL
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(previewUrl)}`, '_blank', 'width=600,height=400');
    toast({ title: "Opening Facebook", description: "Share your listing on Facebook" });
  };

  const sharePreviewToInstagram = async () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    // Instagram doesn't have direct web share, copy link and show instructions
    await navigator.clipboard.writeText(previewUrl);
    toast({ 
      title: "Link copied!", 
      description: "Open Instagram and paste the link in your story or bio" 
    });
  };

  // Twitter and LinkedIn share functions removed - user requested only core platforms

  const sharePreviewViaEmail = () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    const generalListing = listingsByLanguage[0]?.listings?.find(l => l.platform === 'general');
    const title = generalListing?.title || 'Property Listing';
    const body = `Check out this property:\n\n${title}\n\n${previewUrl}`;
    // Use window.location.href for mailto to ensure it opens the email client
    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
    toast({ title: "Opening Email", description: "Share your listing via email" });
  };

  const sharePreviewViaSMS = () => {
    const previewUrl = getPreviewUrl();
    if (!previewUrl) {
      toast({ title: "Save first", description: "Please save the listing to get a shareable link", variant: "destructive" });
      return;
    }
    const generalListing = listingsByLanguage[0]?.listings?.find(l => l.platform === 'general');
    const title = generalListing?.title || 'Check out this property!';
    const message = `${title}\n${previewUrl}`;
    // Use sms: protocol - works on mobile devices
    window.open(`sms:?body=${encodeURIComponent(message)}`, '_self');
    toast({ title: "Opening Messages", description: "Share your listing via SMS" });
  };

  const downloadPhoto = (photo: PropertyPhoto, index: number) => {
    if (blockIfTrial()) return;
    const link = document.createElement('a');
    link.href = photo.enhanced || photo.preview;
    link.download = `property-photo-${index + 1}.jpg`;
    link.click();
  };

  const [isZipping, setIsZipping] = useState(false);

  const downloadAllPhotos = async () => {
    if (blockIfTrial()) return;
    setIsZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder('enhanced-photos')!;

      await Promise.all(
        photos.map(async (photo, index) => {
          const url = photo.enhanced || photo.preview;
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const ext = blob.type.includes('png') ? 'png' : 'jpg';
            folder.file(`photo-${index + 1}.${ext}`, blob);
          } catch {
            console.warn(`Failed to fetch photo ${index + 1}`);
          }
        })
      );

      const content = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `${formData.city || 'listing'}-enhanced-photos.zip`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast({ title: 'Download complete', description: `${photos.length} photos saved as ZIP` });
    } catch (err) {
      console.error('ZIP error:', err);
      toast({ title: 'Download failed', description: 'Could not create ZIP file', variant: 'destructive' });
    } finally {
      setIsZipping(false);
    }
  };

  // Get feature labels from categories
  const featureCategories = formData.listingType === 'rent' ? rentFeatureCategories : saleFeatureCategories;
  const getFeatureLabel = (featureId: string): string => {
    for (const category of featureCategories) {
      const feature = category.features.find(f => f.id === featureId);
      if (feature) return feature.label;
    }
    return featureId;
  };

  const getFeatureIcon = (featureId: string) => {
    for (const category of featureCategories) {
      const feature = category.features.find(f => f.id === featureId);
      if (feature) return feature.icon;
    }
    return null;
  };

  // Update a listing field
  const updateListing = (language: string, platform: string, field: 'title' | 'description', value: string) => {
    setListingsByLanguage(prev => 
      prev.map(langGroup => {
        if (langGroup.language !== language) return langGroup;
        return {
          ...langGroup,
          listings: langGroup.listings.map(listing => {
            if (listing.platform !== platform) return listing;
            return { ...listing, [field]: value };
          })
        };
      })
    );
  };

  const currentLanguageListings = listingsByLanguage.find(l => l.language === selectedLanguage)?.listings || [];
  const facebookListing = currentLanguageListings.find(l => l.platform === 'facebook');
  const generalListing = currentLanguageListings.find(l => l.platform === 'general');
  const instagramListing = currentLanguageListings.find(l => l.platform === 'instagram');
  const instagramStory = currentLanguageListings.find(l => l.platform === 'instagram-story');
  const premiumListing = currentLanguageListings.find(l => l.platform === 'premium');

  const hasEnhancedPhotos = photos.some(p => p.enhanced && p.enhanced !== p.preview);

  const handleSave = () => {
    if (onSave) {
      onSave(listingsByLanguage);
    }
  };

  // Editable text component (respects trial restrictions)
  const EditableText = ({ 
    value, 
    onChange, 
    fieldId,
    isTitle = false,
    className = "",
    copyButton
  }: { 
    value: string; 
    onChange: (value: string) => void; 
    fieldId: string;
    isTitle?: boolean;
    className?: string;
    copyButton?: React.ReactNode;
  }) => {
    const isEditing = editingField === fieldId;
    const [localValue, setLocalValue] = useState(value);

    const handleSave = () => {
      onChange(localValue);
      setEditingField(null);
      toast({ title: "Updated!", description: "Your changes have been saved" });
    };

    const handleCancel = () => {
      setLocalValue(value);
      setEditingField(null);
    };

    const handleEditClick = () => {
      if (isTrial === true) {
        toast({
          title: "Trial Restriction",
          description: "Subscribe to edit descriptions. Trial listings are view-only.",
          variant: "destructive",
        });
        return;
      }
      setLocalValue(value);
      setEditingField(fieldId);
    };

    // Prevent context menu and copy for trial users
    const trialProtectionProps = isTrial === true ? {
      onContextMenu: (e: React.MouseEvent) => { e.preventDefault(); return false; },
      onCopy: (e: React.ClipboardEvent) => { e.preventDefault(); return false; },
      style: { 
        userSelect: 'none' as const,
        WebkitUserSelect: 'none' as const,
      },
    } : {};

    if (isEditing && isTrial !== true) {
      return (
        <div className="space-y-2">
          {isTitle ? (
            <Input
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              className="text-lg font-semibold"
            />
          ) : (
            <Textarea
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              className={`min-h-[200px] text-sm ${className}`}
            />
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>Save</Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="group relative" {...trialProtectionProps}>
        {isTitle ? (
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold text-lg flex-1 ${isTrial === true ? 'select-none' : ''}`}>{value}</h3>
            <Button
              size="sm"
              variant="ghost"
              className="opacity-50 hover:opacity-100 transition-opacity"
              onClick={handleEditClick}
            >
              {isTrial === true ? <Lock className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-muted-foreground hover:text-foreground"
                onClick={handleEditClick}
              >
                {isTrial === true ? <Lock className="w-3.5 h-3.5 mr-1" /> : <Edit3 className="w-3.5 h-3.5 mr-1" />}
                <span className="text-xs">{isTrial === true ? 'Locked' : 'Edit'}</span>
              </Button>
              {/* Show copy button but it will show trial restriction toast */}
              {copyButton}
            </div>
            {isTrial === true && descriptionImageUrl ? (
              // Trial: the description is a baked watermark image — there is no
              // selectable / copyable text in the DOM to scrape.
              <div className="p-3 sm:p-4 bg-muted rounded-xl">
                <img
                  src={descriptionImageUrl}
                  alt="Listing description preview (watermarked)"
                  className="w-full rounded-lg select-none pointer-events-none"
                  draggable={false}
                />
                <div className="mt-3 pt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Subscribe or unlock to get copyable text in every language &amp; platform
                  </p>
                </div>
              </div>
            ) : (
              <div className={`p-3 sm:p-4 bg-muted rounded-xl text-sm leading-relaxed whitespace-pre-wrap max-h-[400px] sm:max-h-[300px] overflow-y-auto ${isTrial === true ? 'select-none' : ''} ${className}`}>
                {value}
                {isTrial === true && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Subscribe to copy this content
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-8 animate-fade-up ${isTrial === true ? 'pb-24' : ''}`}>
      {/* Trial Banner - only show when we KNOW it's a trial (not null/loading) */}
      {isTrial === true && (
        <Card className="border-2 border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background shadow-lg">
          <CardContent className="py-6 px-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                <Lock className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-lg text-foreground">Your listing is ready — but locked</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Trial</span>
                </div>
                <p className="text-sm text-foreground/80 mt-1.5">
                  Unlock everything below in one click — no watermarks, full-quality downloads, copy-paste text, and a shareable property page.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-3 text-sm">
                  <div className="flex items-center gap-2 text-foreground"><Check className="w-4 h-4 text-primary flex-shrink-0" /> Watermark-free photos</div>
                  <div className="flex items-center gap-2 text-foreground"><Check className="w-4 h-4 text-primary flex-shrink-0" /> Copy descriptions to any platform</div>
                  <div className="flex items-center gap-2 text-foreground"><Check className="w-4 h-4 text-primary flex-shrink-0" /> Download HD photos &amp; PDF flyer</div>
                  <div className="flex items-center gap-2 text-foreground"><Check className="w-4 h-4 text-primary flex-shrink-0" /> Public shareable listing page</div>
                </div>
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <Button size="lg" className="gap-2 shadow-md" onClick={handleUnlockKitCheckout} disabled={isUnlockingKit}>
                    {isUnlockingKit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Unlock this listing — {kitPrice}
                  </Button>
                  <Button size="lg" variant="ghost" onClick={() => window.location.href = '/pricing'}>
                    See plans &amp; pricing
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  One-time payment, no subscription. Or subscribe for multiple listings per month.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="text-center space-y-2 px-2">
        <div className="inline-flex items-center gap-2 text-primary mb-2">
          <Sparkles className="w-5 h-5" />
          <span className="text-sm font-medium uppercase tracking-wider">Ready to Post</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-display font-semibold text-foreground">Your Listings are Ready!</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          AI-generated in {listingsByLanguage.length} language{listingsByLanguage.length > 1 ? 's' : ''} • Enhanced photos ready • Click to edit
        </p>
        {/* Saved status badge */}
        {listingId && (
          <>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium mt-2">
              <Check className="w-4 h-4" />
              Listing saved automatically
            </div>

            {/* Post-success "what's next" ladder — surfaces the highest-leverage
                actions immediately so users don't have to scroll to find them */}
            <div className="mt-4 max-w-2xl mx-auto">
              <div className="rounded-xl border border-border bg-card/60 backdrop-blur-sm p-4 sm:p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 text-left">
                  What's next?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      const url = getPreviewUrl();
                      if (url) window.open(url, '_blank');
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2 shrink-0" />
                    View shareable page
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      const url = getPreviewUrl();
                      const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                      if (url) window.open(fbShare, '_blank', 'width=600,height=600');
                    }}
                  >
                    <Facebook className="w-4 h-4 mr-2 shrink-0" />
                    Share on Facebook
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start"
                    onClick={onStartOver}
                  >
                    <Sparkles className="w-4 h-4 mr-2 shrink-0" />
                    Create another listing
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Language Selector */}
      {listingsByLanguage.length > 1 && (
        <Card className="shadow-soft border-0 bg-accent/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Select Language</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {listingsByLanguage.map(({ language }) => (
                <Button
                  key={language}
                  variant={selectedLanguage === language ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLanguage(language)}
                  className="gap-2"
                >
                  <span>{languageLabels[language]?.flag}</span>
                  <span>{languageLabels[language]?.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Property Summary */}
      <Card className="shadow-soft border-0 bg-accent/50">
        <CardContent className="pt-6">
          {createdAt && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
              <Calendar className="w-4 h-4" />
              <span>Created {format(new Date(createdAt), 'MMM d, yyyy · h:mm a')}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full font-medium">
              {formData.listingType === 'rent' ? 'For Rent' : 'For Sale'}
            </span>
            <span className="px-3 py-1 bg-background rounded-full capitalize">{formData.type}</span>
            <span className="px-3 py-1 bg-background rounded-full">{formData.bedrooms === 0 ? 'Studio' : `${formData.bedrooms} bed`}</span>
            <span className="px-3 py-1 bg-background rounded-full">{formData.bathrooms} bath</span>
            {formData.squareMeters && (
              <span className="px-3 py-1 bg-background rounded-full">
                {(() => {
                  const unit = getMarketConfig((profile as any)?.market).areaUnit;
                  const sqm = parseFloat(formData.squareMeters);
                  if (unit === 'sqft' && !isNaN(sqm)) {
                    return `${Math.round(sqm * 10.7639).toLocaleString()} sqft`;
                  }
                  return `${formData.squareMeters} m²`;
                })()}
              </span>
            )}
            <span className="px-3 py-1 bg-background rounded-full">{formData.city}</span>
            {formData.condition && (
              <span className="px-3 py-1 bg-background rounded-full capitalize">{formData.condition.replace('-', ' ')}</span>
            )}
            {formData.furnished && formData.furnished !== 'unfurnished' && (
              <span className="px-3 py-1 bg-background rounded-full capitalize">
                {formData.furnished === 'fully' ? 'Fully Furnished' : 'Partially Furnished'}
              </span>
            )}
            <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full font-medium">
              {(() => {
                const cfg = getMarketConfig((profile as any)?.market);
                const sym = CURRENCY_SYMBOLS[cfg.currency] || `${cfg.currency} `;
                return `${sym}${parseInt(formData.price).toLocaleString()}${formData.listingType === 'rent' ? '/mo' : ''}`;
              })()}
            </span>
          </div>
          {formData.features.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex flex-wrap gap-2 text-xs">
                {formData.features.map(featureId => {
                  const Icon = getFeatureIcon(featureId);
                  return (
                    <span key={featureId} className="px-2 py-1 bg-muted rounded-md flex items-center gap-1.5">
                      {Icon && <Icon className="w-3 h-3 text-primary" />}
                      {getFeatureLabel(featureId)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card className="shadow-elevated border-0 gradient-card">
        <CardHeader className="flex flex-row items-center justify-between px-4 sm:px-6">
          <CardTitle className="text-lg sm:text-xl font-display flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-primary" />
            <span>Enhanced Photos</span>
            {hasEnhancedPhotos && (
              <span className="text-xs font-normal text-muted-foreground ml-1 hidden sm:inline">
                (Click to compare)
              </span>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" className="min-h-[40px] gap-2" onClick={downloadAllPhotos} disabled={isZipping}>
            {isZipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline">{isZipping ? 'Zipping...' : 'Download ZIP'}</span>
            <span className="sm:hidden">{isZipping ? '...' : 'ZIP'}</span>
          </Button>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {photos.map((photo, index) => {
              // Show as enhanced if we have a different enhanced photo and original is available
              const hasEnhancement = photo.enhanced && photo.enhanced !== photo.preview && !photo.originalMissing;
              const isReEnhancing = reEnhancingPhotoId === photo.id;
              
              const photoContent = (
                <div 
                  className={`relative group aspect-square rounded-xl overflow-hidden shadow-soft cursor-pointer ${isReEnhancing ? 'opacity-50' : ''}`}
                >
                  <img 
                    src={photo.enhanced || photo.preview} 
                    alt={`Property photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {isReEnhancing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {hasEnhancement && !isReEnhancing && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-primary/90 text-primary-foreground text-xs rounded-full flex items-center gap-1">
                      <SlidersHorizontal className="w-3 h-3" />
                      Enhanced
                    </div>
                  )}
                  {photo.originalMissing && !isReEnhancing && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500/90 text-white text-xs rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Enhanced Only
                    </div>
                  )}
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/30 transition-colors flex items-center justify-center gap-2">
                    {/* Re-enhance (1 attempt) or choose version dropdown */}
                    {onReEnhancePhoto && !isReEnhancing && (
                      reEnhancedPhotoIds?.has(photo.id) ? (
                        // Already re-enhanced: show dropdown to pick version
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="secondary"
                              className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity w-8 h-8"
                              onClick={(e) => e.stopPropagation()}
                              title="Choose photo version"
                            >
                              <SlidersHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuLabel className="text-xs">Choose Version</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {/* Re-enhanced (new) version */}
                            {onRevertPhoto && (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); onRevertPhoto(photo.id, 'reenhanced'); }}
                                disabled={photo.activeVersion === 'reenhanced' || (!photo.activeVersion && !!photo.reEnhanced)}
                              >
                                <Sparkles className="w-3.5 h-3.5 mr-2" />
                                Re-enhanced version
                                {(photo.activeVersion === 'reenhanced' || (!photo.activeVersion && !!photo.reEnhanced)) && (
                                  <Check className="w-3.5 h-3.5 ml-auto text-primary" />
                                )}
                              </DropdownMenuItem>
                            )}
                            {/* First enhanced version */}
                            {onRevertPhoto && (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); onRevertPhoto(photo.id, 'enhanced'); }}
                                disabled={photo.activeVersion === 'enhanced'}
                              >
                                <ImageIcon className="w-3.5 h-3.5 mr-2" />
                                First enhanced version
                                {photo.activeVersion === 'enhanced' && (
                                  <Check className="w-3.5 h-3.5 ml-auto text-primary" />
                                )}
                              </DropdownMenuItem>
                            )}
                            {/* Original */}
                            {onRevertPhoto && (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); onRevertPhoto(photo.id, 'original'); }}
                                disabled={photo.activeVersion === 'original'}
                              >
                                <RotateCcw className="w-3.5 h-3.5 mr-2" />
                                Original photo
                                {photo.activeVersion === 'original' && (
                                  <Check className="w-3.5 h-3.5 ml-auto text-primary" />
                                )}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        // Not yet re-enhanced: show single re-enhance button
                        <Button
                          size="icon"
                          variant="secondary"
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity w-8 h-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onReEnhancePhoto(photo.id);
                          }}
                          title="Re-enhance this photo"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity min-h-[40px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPhoto(photo, index);
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
              
              return (
                <Dialog key={photo.id}>
                  <DialogTrigger asChild>
                    {isTrial === true ? (
                      <WatermarkOverlay className="rounded-xl">
                        {photoContent}
                      </WatermarkOverlay>
                    ) : (
                      photoContent
                    )}
                  </DialogTrigger>
                  <PhotoDialogContent
                    photos={photos}
                    currentIndex={index}
                    onDownload={downloadPhoto}
                    isTrial={isTrial === true}
                  />
                </Dialog>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* AI-Generated Content - Moved right below photos */}
      <Card className="shadow-elevated border-0 gradient-card">
        <CardHeader className="flex flex-row items-center justify-between px-4 sm:px-6">
          <CardTitle className="text-lg sm:text-xl font-display flex items-center gap-2">
            <span>AI-Generated Content</span>
            {listingsByLanguage.length > 1 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({languageLabels[selectedLanguage]?.flag} {languageLabels[selectedLanguage]?.label})
              </span>
            )}
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            className="min-h-[40px] gap-2"
            onClick={onRegenerate}
            disabled={isRegenerating}
          >
            {isRegenerating ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Regenerate</span>
          </Button>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <Tabs defaultValue="facebook" className="w-full">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
              <TabsList className="inline-flex w-auto min-w-full sm:grid sm:w-full sm:grid-cols-5 mb-4">
                <TabsTrigger value="facebook" className="flex items-center gap-1.5 text-xs sm:text-sm min-h-[40px] px-3">
                  <Facebook className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Facebook</span>
                </TabsTrigger>
                <TabsTrigger value="general" className="flex items-center gap-1.5 text-xs sm:text-sm min-h-[40px] px-3">
                  <Share2 className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">General</span>
                </TabsTrigger>
                <TabsTrigger value="instagram" className="flex items-center gap-1.5 text-xs sm:text-sm min-h-[40px] px-3">
                  <Instagram className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Post</span>
                </TabsTrigger>
                <TabsTrigger value="story" className="flex items-center gap-1.5 text-xs sm:text-sm min-h-[40px] px-3">
                  <ImageIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Story</span>
                </TabsTrigger>
                <TabsTrigger value="premium" className="flex items-center gap-1.5 text-xs sm:text-sm min-h-[40px] px-3">
                  <Crown className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Premium</span>
                </TabsTrigger>
              </TabsList>
            </div>
            
            {/* Facebook Tab */}
            <TabsContent value="facebook" className="space-y-4">
              {facebookListing && (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <EditableText
                      value={facebookListing.title}
                      onChange={(value) => updateListing(selectedLanguage, 'facebook', 'title', value)}
                      fieldId={`title-facebook-${selectedLanguage}`}
                      isTitle
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(facebookListing.title, `copy-title-facebook-${selectedLanguage}`)}
                    >
                      {copiedId === `copy-title-facebook-${selectedLanguage}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div>
                    <EditableText
                      value={facebookListing.description}
                      onChange={(value) => updateListing(selectedLanguage, 'facebook', 'description', value)}
                      fieldId={`desc-facebook-${selectedLanguage}`}
                      copyButton={
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => copyToClipboard(`${facebookListing.title}\n\n${facebookListing.description}`, `full-facebook-${selectedLanguage}`)}
                        >
                          {copiedId === `full-facebook-${selectedLanguage}` ? <><Check className="w-4 h-4 mr-1 text-green-500" />Copied!</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                        </Button>
                      }
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4">
              {generalListing && (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <EditableText
                      value={generalListing.title}
                      onChange={(value) => updateListing(selectedLanguage, 'general', 'title', value)}
                      fieldId={`title-general-${selectedLanguage}`}
                      isTitle
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(generalListing.title, `copy-title-general-${selectedLanguage}`)}
                    >
                      {copiedId === `copy-title-general-${selectedLanguage}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div>
                    <EditableText
                      value={generalListing.description}
                      onChange={(value) => updateListing(selectedLanguage, 'general', 'description', value)}
                      fieldId={`desc-general-${selectedLanguage}`}
                      copyButton={
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => copyToClipboard(`${generalListing.title}\n\n${generalListing.description}`, `full-general-${selectedLanguage}`)}
                        >
                          {copiedId === `full-general-${selectedLanguage}` ? <><Check className="w-4 h-4 mr-1 text-green-500" />Copied!</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                        </Button>
                      }
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Instagram Post Tab */}
            <TabsContent value="instagram" className="space-y-4">
              {instagramListing && (
                <div className="space-y-3">
                  <div>
                    <EditableText
                      value={instagramListing.description}
                      onChange={(value) => updateListing(selectedLanguage, 'instagram', 'description', value)}
                      fieldId={`desc-instagram-${selectedLanguage}`}
                      copyButton={
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => copyToClipboard(instagramListing.description, `instagram-${selectedLanguage}`)}
                        >
                          {copiedId === `instagram-${selectedLanguage}` ? <><Check className="w-4 h-4 mr-1 text-green-500" />Copied!</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                        </Button>
                      }
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Instagram Story Tab */}
            <TabsContent value="story" className="space-y-4">
              {instagramStory && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Text overlay for your Instagram Story:</p>
                  <div>
                    <EditableText
                      value={instagramStory.description}
                      onChange={(value) => updateListing(selectedLanguage, 'instagram-story', 'description', value)}
                      fieldId={`desc-story-${selectedLanguage}`}
                      className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-200"
                      copyButton={
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => copyToClipboard(instagramStory.description, `story-${selectedLanguage}`)}
                        >
                          {copiedId === `story-${selectedLanguage}` ? <><Check className="w-4 h-4 mr-1 text-green-500" />Copied!</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                        </Button>
                      }
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Premium Description Tab */}
            <TabsContent value="premium" className="space-y-4">
              {hasPremiumAccess && premiumListing ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Premium Description</span>
                    <span className="text-xs text-muted-foreground">— Conversion-optimized prose</span>
                  </div>
                  <div>
                    <EditableText
                      value={premiumListing.description}
                      onChange={(value) => updateListing(selectedLanguage, 'premium', 'description', value)}
                      fieldId={`desc-premium-${selectedLanguage}`}
                      className="prose prose-sm max-w-none"
                      copyButton={
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => copyToClipboard(premiumListing.description, `premium-${selectedLanguage}`)}
                        >
                          {copiedId === `premium-${selectedLanguage}` ? <><Check className="w-4 h-4 mr-1 text-green-500" />Copied!</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                        </Button>
                      }
                    />
                  </div>
                </div>
              ) : hasPremiumAccess ? (
                <div className="text-center py-10 space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center mx-auto">
                    <Crown className="w-8 h-8 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Premium Description</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                      Generate a conversion-optimized, research-backed prose description for this listing.
                    </p>
                  </div>
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="gap-2" 
                    disabled={isGeneratingPremium}
                    onClick={async () => {
                      setIsGeneratingPremium(true);
                      try {
                        const descriptions = await generatePremiumDescription(formData, photos, profile);
                        // Add premium listings to each language
                        setListingsByLanguage(prev => 
                          prev.map(langGroup => {
                            const desc = descriptions[langGroup.language];
                            if (desc) {
                              return {
                                ...langGroup,
                                listings: [
                                  ...langGroup.listings,
                                  { platform: 'premium', title: '', description: desc }
                                ]
                              };
                            }
                            return langGroup;
                          })
                        );
                        toast({ title: "Premium description generated!", description: "Your conversion-optimized description is ready." });
                      } catch (err: any) {
                        console.error('Premium generation failed:', err);
                        toast({ title: "Generation failed", description: err.message || "Could not generate premium description.", variant: "destructive" });
                      } finally {
                        setIsGeneratingPremium(false);
                      }
                    }}
                  >
                    {isGeneratingPremium ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4" /> Generate Premium Description</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-10 space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center mx-auto">
                    <Crown className="w-8 h-8 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Premium Description</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                      Get conversion-optimized, research-backed prose descriptions that use proven keywords to increase sale price and reduce time on market.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => window.location.href = '/pricing'}>
                    <Lock className="w-4 h-4" />
                    Upgrade to Growth or Team
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Video Regeneration Banner */}
      {videoUrl && photosReEnhanced && onRegenerateVideo && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5">
          <CardContent className="py-4 px-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Video className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Photos have changed</p>
                <p className="text-xs text-muted-foreground">Your video still uses the previous photos. Regenerate to update it.</p>
              </div>
              <Button 
                size="sm" 
                className="gap-2" 
                onClick={onRegenerateVideo}
                disabled={isRegeneratingVideo}
              >
                {isRegeneratingVideo ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                ) : (
                  <><RefreshCw className="w-4 h-4" /> Update Video</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Property Media Section - Video Only */}
      {videoUrl && (
        <Card className="shadow-elevated border-0 gradient-card">
          <CardHeader>
            <CardTitle className="text-xl font-display flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              Property Video
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Cinematic AI Video add-on banner */}
              {listingId && onGenerateCinematic && cinematicAddon && !cinematicAddon.isTrial && (
                (() => {
                  const purchased = cinematicAddon.purchased;
                  const usedFreeRegen = purchased && cinematicAddon.used > cinematicAddon.paid;
                  const isBusy = !!isGeneratingCinematic;
                  const progressPct = cinematicProgress && cinematicProgress.total > 0
                    ? Math.round((cinematicProgress.done / cinematicProgress.total) * 100)
                    : 0;

                  const resolution = cinematicAddon.resolution ?? '720p';
                  const clipRegensUsed = cinematicAddon.clipRegensUsed ?? 0;
                  const clipRegensAllowance = cinematicAddon.clipRegensAllowance ?? 4;
                  const clipRegensLeft = Math.max(0, clipRegensAllowance - clipRegensUsed);

                  let buttonLabel = cinematicResolution === '1080p'
                    ? 'Unlock Cinematic AI Video — $10 (1080p)'
                    : 'Unlock Cinematic AI Video — $5 (720p)';
                  let helperText = 'Replace the standard slideshow with a cinematic AI-generated property tour — one 6-second shot per photo, stitched into a single MP4 you can post anywhere.';
                  if (purchased && !usedFreeRegen) {
                    buttonLabel = 'Generate Cinematic Video (free regeneration)';
                    helperText = `Cinematic AI Video is unlocked for this listing at ${resolution}. You have 1 free full regeneration and ${clipRegensLeft} free single-clip regenerations left.`;
                  } else if (purchased && usedFreeRegen) {
                    buttonLabel = 'Regenerate Cinematic Video — $5';
                    helperText = `Free full regeneration used. Single-clip regenerations left: ${clipRegensLeft}.`;
                  }

                  return (
                    <div className="rounded-xl border border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 p-4 sm:p-5">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold">Cinematic AI Video</p>
                            <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">Add-on</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{helperText}</p>
                          {!isBusy && !purchased && (
                            <div className="mt-3 grid grid-cols-2 gap-2">
                              {([
                                { id: '720p', title: '720p — $5', desc: 'Great for social and portals.' },
                                { id: '1080p', title: '1080p — $10', desc: 'Full HD, sharper on big screens.' },
                              ] as const).map((opt) => {
                                const selected = cinematicResolution === opt.id;
                                return (
                                  <button
                                    type="button"
                                    key={opt.id}
                                    onClick={() => setCinematicResolution(opt.id)}
                                    className={`text-left rounded-lg border p-2.5 transition-colors ${
                                      selected ? 'border-primary bg-primary/10' : 'border-border bg-background hover:border-primary/40'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${selected ? 'border-primary bg-primary' : 'border-muted-foreground/40'}`} />
                                      <span className="text-xs font-semibold">{opt.title}</span>
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{opt.desc}</p>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {!isBusy && purchased && resolution === '720p' && onUpgradeCinematicResolution && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-3 gap-2"
                              onClick={() => onUpgradeCinematicResolution?.()}
                            >
                              <Crown className="w-4 h-4" /> Upgrade to 1080p — $5
                            </Button>
                          )}
                          {!isBusy && (
                            <div className="mt-3 grid grid-cols-2 gap-2">
                              {([
                                { id: 'walkthrough', title: 'Walkthrough', desc: 'Smooth dolly through every room — feels like you\'re walking the home.' },
                                { id: 'drone', title: 'Drone', desc: 'Aerial-style shots — orbits, crane-ups, fly-throughs.' },
                              ] as const).map((opt) => {
                                const selected = cinematicStyle === opt.id;
                                return (
                                  <button
                                    type="button"
                                    key={opt.id}
                                    onClick={() => onCinematicStyleChange?.(opt.id)}
                                    className={`text-left rounded-lg border p-2.5 transition-colors ${
                                      selected
                                        ? 'border-primary bg-primary/10'
                                        : 'border-border bg-background hover:border-primary/40'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${selected ? 'border-primary bg-primary' : 'border-muted-foreground/40'}`} />
                                      <span className="text-xs font-semibold">{opt.title}</span>
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{opt.desc}</p>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {isBusy && (
                            <div className="mt-3 space-y-1">
                              <p className="text-xs font-medium">
                                {cinematicProgress?.stage === 'stitching'
                                  ? 'Stitching clips into final video…'
                                  : `Generating clip ${cinematicProgress?.done ?? 0} of ${cinematicProgress?.total ?? 0}…`}
                              </p>
                              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary transition-all"
                                  style={{ width: `${cinematicProgress?.stage === 'stitching' ? 95 : progressPct}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => onGenerateCinematic?.(cinematicResolution)}
                            disabled={isBusy}
                            className="gap-2 whitespace-nowrap"
                          >
                            {isBusy ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>
                            ) : (
                              <><Sparkles className="w-4 h-4" /> {buttonLabel}</>
                            )}
                          </Button>
                        </div>
                      </div>
                      {cinematicClips.length > 0 && (
                        <div className="mt-4 border-t border-primary/20 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold">Clips</p>
                            <p className="text-[11px] text-muted-foreground">
                              {clipRegensLeft} free clip regenerations left
                            </p>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {cinematicClips.map((clip) => {
                              const busyClip = regeneratingClipIndex === clip.clipIndex;
                              return (
                                <div key={clip.clipIndex} className="rounded-lg border border-border bg-background overflow-hidden">
                                  <div className="relative aspect-video bg-muted">
                                    {clip.url ? (
                                      <video
                                        src={clip.url}
                                        className="w-full h-full object-cover"
                                        controls
                                        muted
                                        playsInline
                                        preload="metadata"
                                        poster={clip.sourcePhotoUrl}
                                      />
                                    ) : (
                                      <img src={clip.sourcePhotoUrl} alt={`Clip ${clip.clipIndex + 1} source`} className="w-full h-full object-cover opacity-60" />
                                    )}
                                    {busyClip && (
                                      <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-2 flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-medium">
                                      Shot {clip.clipIndex + 1}
                                      {clip.status === 'failed' && <span className="text-destructive"> · failed</span>}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2"
                                      disabled={busyClip || isBusy || clipRegensLeft <= 0}
                                      onClick={() => onRegenerateClip?.(clip.clipIndex)}
                                      title={clipRegensLeft <= 0 ? 'No free clip regenerations left' : 'Regenerate this clip'}
                                    >
                                      <RefreshCw className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Video className="w-4 h-4 text-purple-500" />
                  Video Preview
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (blockIfTrial()) return;
                    try {
                      toast({ title: "Downloading...", description: "Preparing video for download" });
                      // Fetch the video to handle cross-origin URLs
                      const response = await fetch(videoUrl);
                      const blob = await response.blob();
                      const isMp4 = (blob.type || '').includes('mp4') || /\.mp4(\?|$)/i.test(videoUrl);
                      const ext = isMp4 ? 'mp4' : 'webm';
                      const blobUrl = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = blobUrl;
                      link.download = `property-video-${listingId || 'video'}.${ext}`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(blobUrl);
                      toast({ title: "Downloaded!", description: "Video saved successfully" });
                    } catch (error) {
                      console.error('Video download error:', error);
                      toast({ title: "Download failed", description: "Could not download video", variant: "destructive" });
                    }
                  }}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
              <div className="relative aspect-video rounded-xl overflow-hidden shadow-soft bg-muted max-w-3xl mx-auto">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="auto"
                  poster={photos[0]?.enhanced || photos[0]?.preview}
                  onError={(e) => console.error('Video playback error:', e)}
                >
                  <source src={videoUrl} />
                  Your browser does not support video playback.
                </video>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Flyer Download Section with Preview - Only show if user selected PDF flyer option */}
      {profilePictureUrl && (
      <Card className="shadow-elevated border-0 gradient-card">
        <CardHeader>
          <CardTitle className="text-xl font-display flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Property Flyer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Flyer Preview */}
            <Dialog>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  Preview ({languageLabels[initialListingsByLanguage[0]?.language]?.label || 'English'})
                </div>
                <DialogTrigger asChild>
                  <div className="relative aspect-[210/297] rounded-xl overflow-hidden shadow-soft bg-muted group cursor-pointer border">
                    {isLoadingPreview ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : flyerPreviewUrl ? (
                      <>
                        <img 
                          src={flyerPreviewUrl} 
                          alt="Flyer preview"
                          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                        />
                        <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            <span className="text-sm font-medium">View Full Size</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                        <FileText className="w-12 h-12 mb-2 opacity-50" />
                        <span className="text-sm">Preview not available</span>
                      </div>
                    )}
                  </div>
                </DialogTrigger>
                <p className="text-xs text-muted-foreground text-center">Click to view full size preview</p>
              </div>
              <DialogContent className="max-w-3xl p-0 overflow-hidden">
                <DialogHeader className="p-4 pb-2">
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Property Flyer Preview
                  </DialogTitle>
                  <DialogDescription>
                    Professional PDF flyer - download in your preferred language
                  </DialogDescription>
                </DialogHeader>
                <div className="p-4 pt-0 max-h-[80vh] overflow-auto">
                  {flyerPreviewUrl && (
                    <img 
                      src={flyerPreviewUrl} 
                      alt="Flyer preview full size"
                      className="w-full h-auto rounded-lg shadow-md"
                    />
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Download Options */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Download PDF Flyer</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Generate a professional PDF flyer with property photos and details. Select your preferred language for the flyer text.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Flyer Language:</span>
                </div>
                <Select 
                  value={flyerLanguage} 
                  onValueChange={(value) => setFlyerLanguage(value as DescriptionLanguage)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {listingsByLanguage.map((lang) => (
                      <SelectItem key={lang.language} value={lang.language}>
                        <span className="flex items-center gap-2">
                          <span>{languageLabels[lang.language]?.flag}</span>
                          <span>{languageLabels[lang.language]?.label || lang.language}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button
                  onClick={() => generateAndDownloadFlyer(flyerLanguage)}
                  disabled={isGeneratingFlyer}
                  className="w-full"
                  size="lg"
                >
                  {isGeneratingFlyer ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF Flyer
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  PDF will include all property details, photos, and contact info
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Share & Export Section */}
      <Card className="shadow-elevated border-0 gradient-card">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            {/* Share Preview Link - Only for Growth and Team subscribers */}
            {!listingId ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Share2 className="w-4 h-4" />
                <span>Login to enable sharing via WhatsApp, Viber, Telegram, Facebook & Instagram</span>
              </div>
            ) : !hasMicrositeAccess ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-dashed">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/30 dark:to-amber-800/30">
                  <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Share Listing via Social Media</p>
                  <p className="text-xs text-muted-foreground">
                    Upgrade to Growth or Team to share your listing microsite on WhatsApp, Viber, Telegram, Facebook & Instagram
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-9"
                  onClick={() => window.open('/pricing', '_blank')}
                >
                  Upgrade
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <span className="text-sm font-medium text-muted-foreground">Share listing:</span>
                <div className="flex flex-wrap gap-2">
                  {/* Messaging Apps */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-green-50 hover:border-green-300 dark:hover:bg-green-900/20 active:bg-green-50 gap-2"
                    onClick={sharePreviewToWhatsApp}
                  >
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    <span className="text-xs sm:text-sm">WhatsApp</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-purple-50 hover:border-purple-300 dark:hover:bg-purple-900/20 active:bg-purple-50 gap-2"
                    onClick={sharePreviewToViber}
                  >
                    <Phone className="w-4 h-4 text-purple-500" />
                    <span className="text-xs sm:text-sm">Viber</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-sky-50 hover:border-sky-300 dark:hover:bg-sky-900/20 active:bg-sky-50 gap-2"
                    onClick={sharePreviewToTelegram}
                  >
                    <Send className="w-4 h-4 text-sky-500" />
                    <span className="text-xs sm:text-sm">Telegram</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-emerald-50 hover:border-emerald-300 dark:hover:bg-emerald-900/20 active:bg-emerald-50 gap-2"
                    onClick={sharePreviewViaSMS}
                  >
                    <MessageSquare className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs sm:text-sm">SMS</span>
                  </Button>
                  
                  {/* Social Media */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20 active:bg-blue-50 gap-2"
                    onClick={sharePreviewToFacebook}
                  >
                    <Facebook className="w-4 h-4 text-blue-600" />
                    <span className="text-xs sm:text-sm">Facebook</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-pink-50 hover:border-pink-300 dark:hover:bg-pink-900/20 active:bg-pink-50 gap-2"
                    onClick={sharePreviewToInstagram}
                  >
                    <Instagram className="w-4 h-4 text-pink-500" />
                    <span className="text-xs sm:text-sm">Instagram</span>
                  </Button>
                  
                  {/* Email */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 sm:h-9 px-3 hover:bg-orange-50 hover:border-orange-300 dark:hover:bg-orange-900/20 active:bg-orange-50 gap-2"
                    onClick={sharePreviewViaEmail}
                  >
                    <Mail className="w-4 h-4 text-orange-500" />
                    <span className="text-xs sm:text-sm">Email</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Separator */}
            <div className="h-px w-full bg-border" />

            {/* QR Code & Link - Only for Growth and Team subscribers */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {!listingId ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <QrCode className="w-4 h-4" />
                  <span>Login to get QR code & shareable link</span>
                </div>
              ) : !hasMicrositeAccess ? (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-dashed">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/30 dark:to-amber-800/30">
                    <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Shareable Microsite</p>
                    <p className="text-xs text-muted-foreground">
                      Upgrade to Growth or Team to get a shareable link & QR code for your listing
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-9"
                    onClick={() => window.open('/pricing', '_blank')}
                  >
                    Upgrade
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">QR & Link:</span>
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 p-1.5 bg-white rounded-lg shadow-sm border">
                      <img 
                        src={getQrCodeUrl(48)} 
                        alt="QR Code"
                        className="w-[40px] h-[40px]"
                      />
                    </div>
                     <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="h-10 sm:h-9 min-h-[40px]" onClick={downloadQrCode}>
                        <QrCode className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">Download</span> QR
                      </Button>
                      <Button size="sm" variant="outline" className="h-10 sm:h-9 min-h-[40px]" onClick={copyPreviewLink}>
                        <Copy className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">Copy</span> Link
                      </Button>
                      <Button size="sm" variant="outline" className="h-10 sm:h-9 min-h-[40px]" onClick={openPreviewPage}>
                        <ExternalLink className="w-4 h-4 mr-1.5" />
                        Preview
                      </Button>
                      <Button size="sm" variant="ghost" className="h-10 sm:h-9 min-h-[40px]" onClick={() => window.open('/qr-analytics', '_blank')}>
                        <BarChart3 className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">Track</span> Scans
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {hasMicrositeAccess && listingId && (
              <p className="text-xs text-muted-foreground -mt-1">
                This QR code is trackable — every scan is recorded so you can measure performance in{' '}
                <button type="button" onClick={() => window.open('/qr-analytics', '_blank')} className="underline hover:text-foreground">
                  QR Analytics
                </button>
                .
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-4">
        {onSave ? (
          <Button variant="hero" size="lg" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {listingId ? 'Save Changes' : `Save Listing (${listingsByLanguage.length} language${listingsByLanguage.length > 1 ? 's' : ''})`}
              </>
            )}
          </Button>
        ) : null}
        
        <Button variant="outline" size="lg" onClick={onStartOver}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Create Another
        </Button>
      </div>

      {/* Persistent unlock bar for trial listings — the top banner scrolls away,
          this stays pinned so the one-click purchase is always within reach */}
      {isTrial === true && (
        <div className="fixed bottom-0 inset-x-0 z-50 border-t border-primary/20 bg-background/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <div className="container max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Lock className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  This listing is watermarked
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  Unlock HD photos, copyable text &amp; sharing — one-time {kitPrice}, no subscription
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => window.location.href = '/pricing'}>
                See plans
              </Button>
              <Button size="sm" className="gap-1.5 shadow-md sm:h-10 sm:px-5" onClick={handleUnlockKitCheckout} disabled={isUnlockingKit}>
                {isUnlockingKit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Unlock — {kitPrice}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}